<template>
    <span
        class="safe_message_content"
        @click="handleContentClick"
        v-html="safeMessageHtml"
    ></span>
</template>

<script>
export default {
    name: 'SafeMessageContent',

    props: {
        /*
         * Supports:
         * - A message string
         * - A complete message object containing message/content/text/body
         */
        message: {
            type: [
                String,
                Number,
                Object
            ],
            default: ''
        }
    },

    computed: {
        resolvedContent() {
            const content = this.message

            if (
                content === null ||
                typeof content === 'undefined'
            ) {
                return ''
            }

            if (typeof content !== 'object') {
                return String(content)
            }

            const supportedKeys = [
                'message',
                'content',
                'text',
                'body'
            ]

            for (
                let index = 0;
                index < supportedKeys.length;
                index += 1
            ) {
                const key = supportedKeys[index]

                if (
                    Object.prototype.hasOwnProperty.call(
                        content,
                        key
                    ) &&
                    content[key] !== null &&
                    typeof content[key] !== 'undefined'
                ) {
                    return String(content[key])
                }
            }

            return ''
        },

        safeMessageHtml() {
            const content = this.resolvedContent

            if (!content) {
                return ''
            }

            /*
             * Only these backend elements are interpreted:
             *
             * <span>@selected.user</span>
             * <a href="https://example.com">Example</a>
             * <br>
             *
             * Everything else is escaped and displayed as normal text.
             */
            const allowedElementPattern =
                /<span\b[^>]*>[\s\S]*?<\/span>|<a\b[^>]*>[\s\S]*?<\/a>|<br\s*\/?>/gi

            let safeHtml = ''
            let lastIndex = 0
            let match

            while (
                (
                    match =
                        allowedElementPattern.exec(content)
                ) !== null
            ) {
                safeHtml += this.linkifyText(
                    content.slice(
                        lastIndex,
                        match.index
                    )
                )

                safeHtml += this.renderAllowedElement(
                    match[0]
                )

                lastIndex =
                    allowedElementPattern.lastIndex
            }

            safeHtml += this.linkifyText(
                content.slice(lastIndex)
            )

            return safeHtml
        }
    },

    methods: {
        renderAllowedElement(element) {
            if (/^<br\s*\/?>$/i.test(element)) {
                return '<br>'
            }

            /*
             * A mention is styled only when the backend explicitly
             * wraps one valid handle in a span.
             */
            const mentionMatch = element.match(
                /^<span\b[^>]*>\s*(@[A-Za-z0-9][A-Za-z0-9._-]*)\s*<\/span>$/i
            )

            if (mentionMatch) {
                return (
                    '<span class="message_mention">' +
                    this.escapeHtml(mentionMatch[1]) +
                    '</span>'
                )
            }

            const anchorMatch = element.match(
                /^<a\b([^>]*)>([\s\S]*?)<\/a>$/i
            )

            if (anchorMatch) {
                return this.renderBackendLink(
                    anchorMatch[1],
                    anchorMatch[2]
                )
            }

            /*
             * Non-mention spans are not rendered as HTML.
             * Their inner content is displayed as safe normal text.
             */
            return this.linkifyText(
                this.stripHtmlTags(element)
            )
        },

        renderBackendLink(attributes, linkContent) {
            const hrefMatch = String(attributes || '').match(
                /\bhref\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'<>]+))/i
            )

            const originalUrl = hrefMatch
                ? (
                    hrefMatch[1] ||
                    hrefMatch[2] ||
                    hrefMatch[3] ||
                    ''
                )
                : ''

            const decodedUrl = this.decodeHtmlEntities(
                originalUrl
            )

            const safeUrl = this.normalizeUrl(decodedUrl)

            const linkText = this.decodeHtmlEntities(
                this.stripHtmlTags(linkContent)
            ).trim() || decodedUrl

            if (!safeUrl) {
                return this.linkifyText(linkText)
            }

            return this.createSafeLink(
                safeUrl,
                linkText
            )
        },

        linkifyText(value) {
            const text = this.decodeHtmlEntities(value)

            if (!text) {
                return ''
            }

            /*
             * Supports:
             * - https://example.com
             * - http://example.com
             * - www.example.com
             * - user@example.com
             */
            const linkPattern =
                /((?:https?:\/\/|www\.)[^\s<>"']+|[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})/gi

            let result = ''
            let lastIndex = 0
            let match

            while (
                (match = linkPattern.exec(text)) !== null
            ) {
                result += this.escapeHtml(
                    text.slice(
                        lastIndex,
                        match.index
                    )
                )

                let displayValue = match[0]
                let trailingCharacters = ''

                const trailingMatch = displayValue.match(
                    /[.,!?;:)\]}]+$/
                )

                if (trailingMatch) {
                    trailingCharacters = trailingMatch[0]

                    displayValue = displayValue.slice(
                        0,
                        -trailingCharacters.length
                    )
                }

                const isEmail =
                    /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i
                        .test(displayValue)

                let url = displayValue

                if (isEmail) {
                    url = 'mailto:' + displayValue
                } else if (/^www\./i.test(displayValue)) {
                    url = 'https://' + displayValue
                }

                const safeUrl = this.normalizeUrl(url)

                if (safeUrl) {
                    result += this.createSafeLink(
                        safeUrl,
                        displayValue
                    )
                } else {
                    result += this.escapeHtml(
                        displayValue
                    )
                }

                result += this.escapeHtml(
                    trailingCharacters
                )

                lastIndex = linkPattern.lastIndex
            }

            result += this.escapeHtml(
                text.slice(lastIndex)
            )

            return result
        },

        createSafeLink(url, text) {
            const opensInNewTab =
                /^https?:\/\//i.test(url) ||
                /^\/(?!\/)/.test(url)

            const targetAttributes = opensInNewTab
                ? (
                    ' target="_blank"' +
                    ' rel="noopener noreferrer"'
                )
                : ''

            return (
                '<a' +
                ' class="message_hyperlink"' +
                ' href="' + this.escapeHtml(url) + '"' +
                targetAttributes +
                '>' +
                this.escapeHtml(text) +
                '</a>'
            )
        },

        normalizeUrl(value) {
            let url = this.decodeHtmlEntities(value).trim()

            if (!url) {
                return ''
            }

            if (/^www\./i.test(url)) {
                url = 'https://' + url
            }

            // Safe internal application path.
            if (
                /^\/(?!\/)[^\s"'<>]*$/.test(url)
            ) {
                return url
            }

            // External HTTP/HTTPS URL.
            if (
                /^https?:\/\/[^\s"'<>]+$/i.test(url)
            ) {
                return url
            }

            // Email link.
            if (
                /^mailto:[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i
                    .test(url)
            ) {
                return url
            }

            // Telephone link.
            if (
                /^tel:\+?[0-9(). -]{5,}$/i.test(url)
            ) {
                return url
            }

            /*
             * javascript:, data:, file:, protocol-relative URLs
             * and unsupported values are rejected.
             */
            return ''
        },

        handleContentClick(event) {
            let target = event.target

            while (
                target &&
                target !== event.currentTarget
            ) {
                if (
                    target.tagName &&
                    target.tagName.toLowerCase() === 'a'
                ) {
                    /*
                     * Keep a parent message-bubble click handler from
                     * blocking or duplicating the link action.
                     */
                    event.stopPropagation()
                    return
                }

                target = target.parentNode
            }
        },

        stripHtmlTags(value) {
            return String(
                value === null ||
                typeof value === 'undefined'
                    ? ''
                    : value
            ).replace(/<[^>]*>/g, '')
        },

        decodeHtmlEntities(value) {
            return String(
                value === null ||
                typeof value === 'undefined'
                    ? ''
                    : value
            )
                .replace(/&quot;/gi, '"')
                .replace(/&#0*39;|&#x0*27;/gi, "'")
                .replace(/&lt;/gi, '<')
                .replace(/&gt;/gi, '>')
                .replace(/&amp;/gi, '&')
        },

        escapeHtml(value) {
            return String(
                value === null ||
                typeof value === 'undefined'
                    ? ''
                    : value
            )
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#039;')
        }
    }
}
</script>

<style lang="scss">
.safe_message_content {
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    word-break: break-word;
    font-size: 14px;
    line-height: 1.2;
    padding-right: 14px;
}

.safe_message_content .message_mention {
    color: #18027e;
    font-weight: 600;
}

.safe_message_content .message_hyperlink {
    color: #18027e;
    text-decoration: none;
    overflow-wrap: anywhere;
    word-break: break-word;
    cursor: pointer;
    pointer-events: auto;
}
</style>